import React, { useEffect, useRef } from 'react';
import Column from '@amzn/meridian/column';
import Divider from '@amzn/meridian/divider';
import Text from '@amzn/meridian/text';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'src/arb-wrapper';
import { liveStatusRank } from 'src/constants';
import type { FormRhfControllerProps } from 'src/components/common/forms/types';
import { LiveStatusesConfig } from 'src/components/campaigns/forms/TemplateSelectUtils';
import { TemplateScheduleWindowInput } from 'src/components/campaigns/forms/TemplateScheduleWindowInput';

// FIXED window: N days before the event, at a wall-clock time in the event-local timezone.
const makeFixedWindow = (daysBeforeEvent: number, timeOfDay = '00:00:00') =>
  ({ mode: 'FIXED' as const, daysBeforeEvent, timeOfDay });

// EVENT window: an offset relative to the event start. offset 0 means "at event time".
const makeEventWindow = (offset: number, direction: 'BEFORE' | 'AFTER') =>
  ({ mode: 'EVENT' as const, offset, unit: 'HOURS' as const, direction, anchor: 'EVENT_START' as const });

// Per-status default schedule windows. Factories, not shared constants: each seeded status gets its
// OWN nested goesLive/takenDown objects so no two statuses ever alias the same mutable object. Unknown
// statuses fall back to the all-zeros FIXED window.
const DEFAULT_SCHEDULE_WINDOW_FACTORIES: Record<string, () => { goesLive: object; takenDown: object }> = {
  // Uploaded 3 days before event at 12AM, taken down 1 day before event at 12AM.
  COMING_SOON: () => ({ goesLive: makeFixedWindow(3), takenDown: makeFixedWindow(1) }),
  // Uploaded 1 day before event at 12AM, taken down 0 days before event at 12AM.
  TOMORROW: () => ({ goesLive: makeFixedWindow(1), takenDown: makeFixedWindow(0) }),
  // Uploaded 0 days before event at 12AM, taken down at event time.
  TODAY: () => ({ goesLive: makeFixedWindow(0), takenDown: makeEventWindow(0, 'AFTER') }),
  // Uploaded at event time, taken down 2 hours after event time.
  LIVE_NOW: () => ({ goesLive: makeEventWindow(0, 'AFTER'), takenDown: makeEventWindow(2, 'AFTER') }),
};

const makeDefaultScheduleWindow = (status: string) =>
  (DEFAULT_SCHEDULE_WINDOW_FACTORIES[status] ??
    (() => ({ goesLive: makeFixedWindow(0), takenDown: makeFixedWindow(0) })))();

// Order checked live statuses by their canonical LIVE_STATUSES position so the schedule blocks
// match the checkbox order regardless of the order the user selected them in. Unknown statuses
// (defensive) sort to the end.
const orderLiveStatuses = (statuses: string[]): string[] =>
  [...statuses].sort(
    (first, second) => liveStatusRank(first) - liveStatusRank(second),
  );

interface StrategyScheduleWindowsProps extends Pick<FormRhfControllerProps, 'control'> {
  /** Indexed product-strategy path, e.g. `productStrategies.0`. */
  strategyName: string;
  /** IANA timezone from the selected publisher account config; labels the FIXED time inputs. */
  publisherTimezone: string;
}

/**
 * Renders a single sports schedule editor per product strategy: the live-status checkboxes plus one
 * goesLive/takenDown window per checked status. The values are canonically stored at the strategy
 * level (`${strategyName}.liveStatuses` / `${strategyName}.scheduleWindows`) so the user enters the
 * schedule once; `applyStrategyScheduleToTemplates` (in FormCampaignEdit) fans them onto every bender
 * template at submit time, keeping the persisted per-template shape the backend and read side expect.
 */
export const StrategyScheduleWindows: React.FC<StrategyScheduleWindowsProps> = ({
  control,
  strategyName,
  publisherTimezone,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const liveStatusesPath = `${strategyName}.liveStatuses`;
  const scheduleWindowsPath = `${strategyName}.scheduleWindows`;
  const liveStatuses = useWatch({ control, name: liveStatusesPath }) as string[] | undefined;
  const scheduleWindows = useWatch({ control, name: scheduleWindowsPath }) as Record<string, unknown> | undefined;

  // Edit mode: existing campaigns store the schedule per bender template. Hoist the first template's
  // values up to the strategy level once on mount so the single editor is pre-filled. Guarded so it
  // never clobbers values the user is actively editing (or a create flow's fresh state).
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const alreadyHoisted = getValues(liveStatusesPath) !== undefined || getValues(scheduleWindowsPath) !== undefined;
    if (alreadyHoisted) return;

    const templates = (getValues(`${strategyName}.benderTemplates`) ?? []) as Array<{
      config?: { liveStatuses?: string[] };
      scheduleWindows?: Record<string, unknown>;
    }>;
    const seed = templates.find(
      (tpl) => (tpl?.config?.liveStatuses?.length ?? 0) > 0 ||
        (tpl?.scheduleWindows && Object.keys(tpl.scheduleWindows).length > 0),
    );
    if (!seed) return;

    if (seed.config?.liveStatuses) setValue(liveStatusesPath, seed.config.liveStatuses);
    if (seed.scheduleWindows) setValue(scheduleWindowsPath, seed.scheduleWindows);
  }, [strategyName, liveStatusesPath, scheduleWindowsPath, getValues, setValue]);

  // Seed a default schedule window for each newly checked live status. Windows for unchecked statuses
  // are intentionally NOT deleted here — they stay in form state (hidden) so re-checking a status
  // within the session restores the values the user had set. Stale windows are pruned at submit time
  // (see pruneScheduleWindows in FormCampaignEdit).
  useEffect(() => {
    const current = (getValues(scheduleWindowsPath) ?? {}) as Record<string, unknown>;
    const checked = liveStatuses ?? [];

    const missing = checked.filter((status) => !current[status]);
    if (missing.length === 0) return;

    const next = { ...current };
    for (const status of missing) {
      next[status] = makeDefaultScheduleWindow(status);
    }
    setValue(scheduleWindowsPath, next, { shouldDirty: true });
  }, [liveStatuses, scheduleWindowsPath, getValues, setValue]);

  return (
    <Column data-testid="strategy-schedule-editor" spacing="300" spacingInset="300 none">
      <LiveStatusesConfig control={control} name={liveStatusesPath} />
      {control && (liveStatuses?.length ?? 0) > 0 && (
        <Column spacing="200">
          <Text color="primary" type="b300">{t('asset_detail_schedule_title')}</Text>
          {orderLiveStatuses(liveStatuses!)
            // Only render a status block once its window has been seeded into form state. Otherwise the
            // nested useController inputs would register their own all-zeros defaults on first render,
            // beating the seed effect and clobbering the per-status default (goesLive/takenDown offsets).
            .filter((status) => scheduleWindows?.[status] !== undefined)
            .map((status) => (
              <Column data-testid={`strategy-schedule-${status}`} key={status} spacing="100">
                <Divider />
                <Text color="secondary" type="b200">{status.replace(/_/g, ' ')}</Text>
                <TemplateScheduleWindowInput
                  control={control}
                  publisherTimezone={publisherTimezone}
                  scheduleWindowPath={`${scheduleWindowsPath}.${status}`}
                />
              </Column>
            ))}
        </Column>
      )}
    </Column>
  );
};
